"use strict";
// P5 UI Polish W3 T2 — Scheduled-assignment contracts (spec §4.1, §4.2).
//
// ScheduledAssignment is the single source of truth for composer × player
// (or group) × time-window + RRULE recurrence + priority. The /me/
// assignment resolver (server-side) walks active rows and picks the
// lowest-priority match for "now".
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScheduledAssignmentInstance = exports.ScheduledAssignmentRow = exports.UpdateScheduledAssignmentInput = exports.CreateScheduledAssignmentInput = exports.ScheduledAssignmentState = void 0;
const zod_1 = require("zod");
exports.ScheduledAssignmentState = zod_1.z.enum([
    'active',
    'paused',
    'cancelled',
    'expired',
]);
const baseFields = zod_1.z.object({
    composer_id: zod_1.z.string().uuid(),
    composer_version_id: zod_1.z.string().uuid().nullish(),
    starts_at: zod_1.z.string().datetime(),
    ends_at: zod_1.z.string().datetime().nullish(),
    rrule: zod_1.z.string().nullish(),
    /// RFC 5545 EXRULE — excludes occurrences from RRULE expansion (W5 T4).
    exrule: zod_1.z.string().nullable().optional(),
    /// RFC 5545 EXDATE — array of ISO 8601 timestamp strings; excludes specific dates from RRULE expansion (W6 T1).
    exdate: zod_1.z.array(zod_1.z.string().datetime()).nullable().optional(),
    priority: zod_1.z.number().int().min(0).max(999).default(100),
});
exports.CreateScheduledAssignmentInput = baseFields
    .extend({
    player_id: zod_1.z.string().uuid().nullish(),
    group_id: zod_1.z.string().uuid().nullish(),
    // FS §8.5 — audited force-assign. When force===true the service wraps
    // the create in a $transaction with a 'composer_assigned_force'
    // audit_event. Non-force payload byte-unchanged (no new required field,
    // no behaviour change) — existing callers untouched.
    force: zod_1.z.boolean().optional(),
    reason: zod_1.z.string().min(3).optional(),
})
    .refine((v) => (v.player_id != null) !== (v.group_id != null), { message: 'Exactly one of player_id or group_id must be set' })
    .refine((v) => !(v.force === true && (v.reason == null || v.reason.trim().length < 3)), { message: 'reason_required_when_force' });
exports.UpdateScheduledAssignmentInput = zod_1.z.object({
    starts_at: zod_1.z.string().datetime().optional(),
    ends_at: zod_1.z.string().datetime().nullable().optional(),
    rrule: zod_1.z.string().nullable().optional(),
    /// RFC 5545 EXRULE — excludes occurrences from RRULE expansion (W5 T4).
    exrule: zod_1.z.string().nullable().optional(),
    /// RFC 5545 EXDATE — array of ISO 8601 timestamp strings; excludes specific dates from RRULE expansion (W6 T1).
    exdate: zod_1.z.array(zod_1.z.string().datetime()).nullable().optional(),
    priority: zod_1.z.number().int().min(0).max(999).optional(),
    state: exports.ScheduledAssignmentState.optional(),
    /** Reassign to a different player (clears group_id). Exactly one of player_id
     *  or group_id may be supplied per request. */
    player_id: zod_1.z.string().uuid().optional(),
    /** Reassign to a different group (clears player_id). Exactly one of player_id
     *  or group_id may be supplied per request. */
    group_id: zod_1.z.string().uuid().optional(),
}).refine((v) => !(v.player_id != null && v.group_id != null), { message: 'Cannot set both player_id and group_id in the same update' });
exports.ScheduledAssignmentRow = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    tenant_id: zod_1.z.string().uuid(),
    player_id: zod_1.z.string().uuid().nullable(),
    group_id: zod_1.z.string().uuid().nullable(),
    composer_id: zod_1.z.string().uuid(),
    composer_version_id: zod_1.z.string().uuid().nullable(),
    starts_at: zod_1.z.string().datetime(),
    ends_at: zod_1.z.string().datetime().nullable(),
    rrule: zod_1.z.string().nullable(),
    /// RFC 5545 EXRULE — excludes occurrences from RRULE expansion (W5 T4).
    exrule: zod_1.z.string().nullable().optional(),
    /// RFC 5545 EXDATE — array of ISO 8601 timestamp strings; excludes specific dates from RRULE expansion (W6 T1).
    exdate: zod_1.z.array(zod_1.z.string().datetime()).nullable().optional(),
    priority: zod_1.z.number().int(),
    created_by_id: zod_1.z.string().uuid(),
    created_at: zod_1.z.string().datetime(),
    state: exports.ScheduledAssignmentState,
});
/** Materialized instance from /expand — single time window. */
exports.ScheduledAssignmentInstance = zod_1.z.object({
    scheduled_assignment_id: zod_1.z.string().uuid(),
    composer_id: zod_1.z.string().uuid(),
    composer_version_id: zod_1.z.string().uuid().nullable(),
    starts_at: zod_1.z.string().datetime(),
    ends_at: zod_1.z.string().datetime(),
    priority: zod_1.z.number().int(),
});
//# sourceMappingURL=scheduling.schema.js.map